import { useViewportHeight } from '@/Utility/hooks';
import { ZnglyVideoMediaNameEnum } from '@/graphql/types';
import type { AppStore } from '@/inertia/AppStore';
import { Fullscreen, FullscreenExit } from '@mui/icons-material';
import { Box, IconButton, Stack } from '@mui/material';
import { useCallback, useEffect, useMemo } from 'react';
import { usePlayerRef } from './Player';
import { usePlayerStore } from './usePlayerStore';

type Props = {
  node: AppStore['playerNode'];
};

/**
 * Player Bar sits at the bottom of the player and contains the player controls
 */
const PlayerBar = (props: Props) => {
  const showPlayerBar = usePlayerStore((state) => state.showPlayerBar);
  const disabledPlayerBar = usePlayerStore((state) => state.disabledPlayerBar);
  const isFullScreen = usePlayerStore((state) => state.isFullScreen);
  const viewPortHeight = useViewportHeight();

  const fullScreenStyles = useMemo(() => {
    if (!isFullScreen) return {};

    return {
      top: viewPortHeight - 30,
    };
  }, [isFullScreen, viewPortHeight]);

  const ActionBar = useMemo(() => {
    if (props?.node?.znglyContent?.contentMediaName?.toUpperCase?.() === ZnglyVideoMediaNameEnum.Pdf) {
      return PdfBar;
    }

    return null;
  }, [props?.node?.znglyContent?.contentMediaName]);

  if (ActionBar === null) return null;
  if (disabledPlayerBar) return null;

  return (
    <Box
      className="player-bar"
      sx={{
        visibility: showPlayerBar || isFullScreen ? 'visible' : 'hidden',
        position: 'absolute',
        bottom: 0,
        ...fullScreenStyles,
        left: 0,
        right: 0,
        backgroundColor: 'white',
        borderRadius: '6px 6px 0px 0px',
        height: '30px',
        p: 0,
        zIndex: 1,
        opacity: 0.7,
        boxShadow: '0px -1px 5px 0px rgba(0,0,0,0.75)',
        transition: 'all 0.3s',
        '&:hover': {
          opacity: 1,
          transition: 'all 0.3s',
        },
      }}
    >
      <ActionBar />
    </Box>
  );
};

type GenericBarProps = {
  children: {
    left?: React.ReactNode;
    center?: React.ReactNode;
    right?: React.ReactNode;
  };
};
const GenericBar = ({ children: { left = <></>, center = <></>, right = <></> } }: GenericBarProps) => {
  const playerRef = usePlayerRef();

  const handleMouseMove = useCallback(() => {
    usePlayerStore.setState({ showPlayerBar: true });
  }, []);

  const handleMouseLeave = useCallback(() => {
    usePlayerStore.setState({ showPlayerBar: false });
  }, []);

  const handleMouseEnter = useCallback(() => {
    usePlayerStore.setState({ showPlayerBar: true });
  }, []);

  // listen to the document exit fullscreen event
  const handleFullScreenChange = useCallback(() => {
    if (!document.fullscreenElement) {
      usePlayerStore.setState({ isFullScreen: false });
    }
  }, []);

  useEffect(() => {
    if (!playerRef?.current) return;

    document.addEventListener('fullscreenchange', handleFullScreenChange);
    playerRef.current?.querySelector('.Wrapper')?.addEventListener('mousemove', handleMouseMove);
    playerRef.current?.querySelector('.Wrapper')?.addEventListener('mouseleave', handleMouseLeave);
    playerRef.current?.querySelector('.Wrapper')?.addEventListener('mouseenter', handleMouseEnter);

    return () => {
      playerRef.current?.querySelector('.Wrapper')?.removeEventListener('mousemove', handleMouseMove);
      playerRef.current?.querySelector('.Wrapper')?.removeEventListener('mouseleave', handleMouseLeave);
      playerRef.current?.querySelector('.Wrapper')?.removeEventListener('mouseenter', handleMouseEnter);
      document.removeEventListener('fullscreenchange', handleFullScreenChange);
    };
  }, [playerRef.current]);

  return (
    <Stack
      direction="row"
      spacing={2}
      sx={{ padding: '10px', height: '100%' }}
      p={0}
      alignItems="center"
      justifyContent="space-between"
    >
      <Stack justifyContent="flex-start">{left}</Stack>
      <Stack justifyContent="center">{center}</Stack>
      <Stack justifyContent="flex-end">{right}</Stack>
    </Stack>
  );
};

const PdfBar = () => {
  const isFullScreen = usePlayerStore((state) => state.isFullScreen);
  const playerRef = usePlayerRef();

  const toggleFullScreen = async () => {
    if (!playerRef) return;

    usePlayerStore.setState({ isFullScreen: !isFullScreen });
    if (!isFullScreen) {
      await playerRef.current?.firstElementChild?.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  return (
    <GenericBar>
      {{
        right: (
          <IconButton
            disabled={!playerRef}
            onClick={toggleFullScreen}
            sx={{
              p: 0,
              justifySelf: 'flex-end',
            }}
          >
            {isFullScreen ? <FullscreenExit /> : <Fullscreen />}
          </IconButton>
        ),
      }}
    </GenericBar>
  );
};

export default PlayerBar;
